import React, { Component, createRef } from "react";
import { Spin, Divider } from "antd";
import { getPlaneCapacitanceResult } from "../../../../../services/Cascade/Impedance";
import ResultLayout from "../../../../../services/Result/Public/resultLayout";
import ResultRightMenu from "../../../../../services/Result/Public/resultRightMenu";
import WaveformSetting from '../../../../../services/Result/Public/waveform/waveformSetting';
import WaveformSelection from "./waveformSelection";
import '../index.css';
import getIndex from "../../../../../services/helper/insertionSearch";
import { parsePlaneCapacitanceCSV } from "../../../../../services/Cascade/result/planeCapacitance";
import { CapacitancePlot } from "../../../../../services/Cascade/result/planeCapacitance/capacitancePlot";
import defaultColor from '@/constants/defaultColors';
import { numConversion } from '../../../../../services/helper/dataProcess';
import { scaleConversion } from '../../../../../services/helper/numberHelper';
import NP from 'number-precision';
import { IMPEDANCE_PACKAGE, IMPEDANCE_PCB } from "../../../../../services/Cascade/constants";
import { yAxisValueFormat } from "../../../../../services/Result/Public/sparameter/dataHelper";

const defaultAxis = {
  ui: 1,
  xUnit: "Hz",
  yUnit: "F",
  xMin: "",
  xMax: "",
  yMin: "",
  yMax: "",
  xLabel: "Frequency",
  yLabel: "Capacitance",
  xScale: "linear",
  yScale: "linear"
}

class PlaneCapacitance extends Component {
  constructor(props) {
    super(props)
    this.svgRef = createRef();
    this.state = {
      loading: true,
      compInfo: [],
      resultList: [],
      axis: {
        ...defaultAxis
      }
    }
    this.capacitancePlot = new CapacitancePlot()
    this.changeAxis = this.changeAxis.bind(this);
  }

  onRef = (ref) => {
    this.waveformSetting = ref;
  }

  componentDidMount = () => {
    window.removeEventListener('resize', this.resize);
    this.getPlaneCapacitanceData()
  }

  initList = () => {
    const { ImpedanceContent, impedanceResultContent } = this.props;
    let resultList = []
    const content = impedanceResultContent ? impedanceResultContent.layouts : null;
    for (let item of (content || ImpedanceContent || [])) {
      if ([IMPEDANCE_PCB, IMPEDANCE_PACKAGE].includes(item.type)) {
        resultList.push({
          designId: item.designId || "",
          designName: item.designName || ""
        })
      }
    }
    return resultList
  }

  generateD3Curves = (csvContent, designId) => {
    const { frequencies, curveData } = parsePlaneCapacitanceCSV(csvContent);
    const signals = Object.keys(curveData);
    const sigDataList = signals.map((signal, index) => ({
      signal,
      color: defaultColor[index % defaultColor.length]
    }));
    const curves = sigDataList.map(sigData => {
      const curve = this.capacitancePlot.setCurve({
        name: `${designId}-${sigData.signal}`,
        x: frequencies,
        y: curveData[sigData.signal] || [],
        visible: true,
        color: sigData.color || '#000'
      });
      return curve;
    });
    return curves;
  }

  getPlaneCapacitanceData = async () => {
    const { axis } = this.state
    const { id } = this.props
    try {
      const resultList = this.initList()
      let _resultList = JSON.parse(JSON.stringify(resultList))

      const promises = resultList.map(async (item) => {
        try {
          const res = await getPlaneCapacitanceResult(id, item.designId);
          if (!res) {
            _resultList = _resultList.filter(it => it.designId !== item.designId)
            return []
          }
          return this.generateD3Curves(res, item.designId);
        } catch (err) {
          console.error(err);
          _resultList = _resultList.filter(it => it.designId !== item.designId)
          return [];
        }
      });

      const curves = (await Promise.all(promises)).flat();

      if (!curves.length) {
        this.setState({ loading: false });
        return;
      }

      const compInfo = curves.map((curve, index) => {
        const info = curve.name.split('-');
        const designId = info.length ? info[0] : "";
        const name = info.length ? info[1] : "";
        return { ...curve, curveIndex: index, designId, name };
      });

      const events = {
        changeMouse: this.changeMouse,
        cancelMove: this.cancelMove,
        changeAxis: this.changeAxis,
      };
      this.capacitancePlot.plotCapacitance({ svgElement: this.svgRef.current, events, axis, id: "cascade_plane_capacitance_result" });
      this.capacitancePlot.plotCurves(curves)
      this.plot2D = this.capacitancePlot.plot
      setTimeout(() => {
        this.capacitancePlot.updateCurvePlot()
      }, 100)
      this.setState({
        loading: false,
        compInfo,
        selectedKeys: curves.map(item => item.name),
        resultList: _resultList
      })
    } catch (e) {
      this.setState({ loading: false })
      console.error(e)
    }
  }

  resultRightMenuOnRef = (ref) => {
    this.resultRightMenu = ref;
  }

  changeAxis = (axis) => {
    this.waveformSetting.setAxis(axis);
  }

  axisChangeCB = (type, { _xMin, _xMax, _yMin, _yMax, xUnit, yUnit }) => {
    if (!this.plot2D) {
      return
    }
    const plot = this.plot2D;
    if (type === 'xMin' || type === 'xMax') {
      const scale = scaleConversion("Hz", xUnit);
      const xMax = plot.options.xMax,
        xMin = plot.options.xMin,
        start = NP.times(_xMin, scale),
        end = NP.times(_xMax, scale);
      plot.xScale
        .domain([Math.min(start, end), Math.max(start, end)])
        .range([0, plot.size.width]);

      const xrange = plot.xScale.domain(),
        width = plot.size.width,
        startX = NP.times(NP.divide(NP.minus(xrange[0], xMin), NP.minus(xMax, xMin)), width),
        endX = NP.times(NP.divide(NP.minus(xrange[1], xMin), NP.minus(xMax, xMin)), width);
      plot.updateRange(startX, endX);
    } else {
      // y axis
      const _yUnit = yUnit.replace("F", "")
      const scale = scaleConversion("F", _yUnit);
      const yStart = NP.times(numConversion(parseFloat(_yMin)), scale),
        yEnd = NP.times(numConversion(parseFloat(_yMax)), scale);
      if (plot.scale || {}.y === 'log') {
        let min = Math.min(yStart, yEnd)
        let max = Math.max(yStart, yEnd)
        if (min <= 0) min = 1e-9;
        if (max <= 0) max = 1e-9;
        plot.yScale
          .domain([max, min])
          .range([0, plot.size.height])
      } else {
        plot.yScale
          .domain([Math.max(yStart, yEnd), Math.min(yStart, yEnd)])
          .nice()
          .range([0, plot.size.height])
          .nice();
      }
    }
    this.reloadAxisCB()
  }

  reloadAxisCB = () => {
    this.plot2D.updatePlot();
  }

  _getIndex(xPoints, freq) {
    return getIndex(xPoints, freq, 0, xPoints.length - 1);
  }

  colorChange = (e, curveIndex) => {
    const value = e.toHexString();
    if (value) {
      const curves = this.capacitancePlot.getCurves()
      const { compInfo } = this.state;
      const findIndex = compInfo.findIndex(item => item.curveIndex === curveIndex)
      if (findIndex > -1) {
        compInfo[findIndex].color = value;
      }
      curves[curveIndex].color = value;
      this.setState({
        compInfo
      }, () => {
        this.reloadAxisCB()
      });
    }
  }

  changeMouse = (frequency, yPrefix) => {
    if (!this.plot2D) {
      return;
    }
    const { compInfo } = this.state
    const curves = this.capacitancePlot.getCurves();
    for (let k = 0; k < curves.length; k++) {
      if (!curves[k].visible) {
        continue;
      }
      const compInfoIndex = compInfo.findIndex(item => item.curveIndex === k);
      if (compInfoIndex < 0) { continue }

      const index = this._getIndex(curves[k].x, frequency);
      const rawValue = index > -1 ? curves[k].y[index] : null;
      let current = "";
      if (rawValue != null) {
        const formatted = yPrefix(rawValue);
        const num = parseFloat(formatted);
        const unit = formatted.replace(/[0-9\.\-e+]/g, '');
        let displayValue = yAxisValueFormat(num)
        current = displayValue + unit + "F";
      }
      compInfo[compInfoIndex].current = current;
    }
    this.setState({
      compInfo
    })
  }

  cancelMove = () => {
    const curves = this.capacitancePlot.getCurves()
    const { compInfo } = this.state;
    for (let k = 0; k < curves.length; k++) {
      const compInfoIndex = compInfo.findIndex(item => item.curveIndex === k);
      if (compInfoIndex < 0) { continue }
      if (curves[k].visible) {
        compInfo[compInfoIndex].current = '';
      }
    };
    this.setState({
      compInfo
    })
  }

  changeSelectedKeys = (keys) => {
    let curves = this.capacitancePlot.getCurves()
    curves.forEach((curve, i) => {
      curve.visible = keys.includes(curve.name)
    });
    this.setState({
      selectedKeys: keys
    }, () => {
      this.reloadAxisCB()
    })
  }

  scaleChangeCB = (type, scale) => {
    if (!this.plot2D) {
      return
    }
    this.plot2D.changeScale(type, scale);
  }

  resultListRender = () => {
    const { compInfo, selectedKeys, resultList } = this.state;
    return (
      <WaveformSelection
        compInfo={compInfo}
        colorChange={this.colorChange}
        selectedKeys={selectedKeys}
        changeSelectedKeys={this.changeSelectedKeys}
        resultList={resultList}
      />
    )
  }

  settingRender = () => {
    return <div className='cascade-waveform-setting-box'>
      <Divider orientation="left" className='waveform-setting-title'>Setting</Divider>
      <WaveformSetting
        onRef={this.onRef}
        plot={this.plot2D}
        axisChangeCB={this.axisChangeCB}
        reloadAxisCB={this.reloadAxisCB}
        currentResultKey={'CAPACITANCE'}
        scaleChangeCB={this.scaleChangeCB}
        showScale={true}
      />
    </div>
  }

  resultLeft = () => {
    const { loading } = this.state;
    return (
      <Spin spinning={loading} size='large'>
        <div className='cascade-result-parameter-left'>
          <svg ref={this.svgRef}></svg>
        </div>
      </Spin>
    )
  }

  resultRight = () => {
    return (
      <ResultRightMenu
        onRef={this.resultRightMenuOnRef}
        resultList={this.resultListRender}
        setting={this.settingRender}
      />
    )
  }

  changeWidthCB = () => {
    if (this.plot2D) {
      this.plot2D.redrawPlot(this.svgRef.current);
    }
  }

  render() {
    return (
      <ResultLayout
        defaultRightWidth={330}
        resultLeft={this.resultLeft}
        resultRight={this.resultRight}
        changeWidthCB={this.changeWidthCB}
      />
    )
  }
}
export default PlaneCapacitance;